import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from './Sidebar';
import canteenService from '../services/canteenService';
import api from '../services/api';
import './CanteenScreen.css';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const TODAY       = new Date();
const capFirst = v => v ? v.charAt(0).toUpperCase() + v.slice(1) : v;

// ── helpers ──────────────────────────────────────────────────────────────────
const fmtDate  = (y, m, d) => `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
const daysInMonth = (y, m) => new Date(y, m, 0).getDate(); // m is 1-based

// Build a lookup map:  "yyyy-MM-dd" → { b, l, d, note }
function buildLookup(menus) {
  const map = {};
  menus.forEach(m => {
    const key = m.date;
    if (!map[key]) map[key] = {};
    const slot = { name: m.mealName, items: m.items || [], id: m.id, status: m.status };
    if (m.mealType === 'BREAKFAST') map[key].b = slot;
    if (m.mealType === 'LUNCH')     map[key].l = slot;
    if (m.mealType === 'DINNER')    map[key].d = slot;
    if (m.specialNote)              map[key].note = m.specialNote;
  });
  return map;
}

// ── MEAL SLOT PILL ────────────────────────────────────────────────────────────
const MealPill = ({ type, data }) => {
  if (!data?.name) return null;
  const cls = type === 'b' ? 'breakfast' : type === 'l' ? 'lunch' : 'dinner';
  const lbl = type === 'b' ? 'Breakfast' : type === 'l' ? 'Lunch' : 'Dinner';
  return (
    <div className={`meal-slot ${cls}`}>
      <div className="meal-label">{lbl}</div>
      <div className="meal-name">{data.name}</div>
    </div>
  );
};

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function CanteenScreen({ onNavigate, onLogout }) {
  const [view,        setView]        = useState('calendar'); // 'calendar' | 'planner'
  const [activeTab,   setActiveTab]   = useState('all');
  const [calYear,     setCalYear]     = useState(TODAY.getFullYear());
  const [calMonth,    setCalMonth]    = useState(TODAY.getMonth() + 1); // 1-based
  const [planYear,    setPlanYear]    = useState(TODAY.getFullYear());
  const [planMonth,   setPlanMonth]   = useState(TODAY.getMonth() + 1);
  const [menus,       setMenus]       = useState({}); // { "yyyy-MM-dd": { b, l, d, note } }
  const [planMenus,   setPlanMenus]   = useState({}); // same shape for planner
  const [monthStatus, setMonthStatus] = useState('EMPTY'); // EMPTY | DRAFT | PUBLISHED
  const [todayMenus,  setTodayMenus]  = useState({});
  const [admStats,    setAdmStats]    = useState({ total:0, allocated:0, currentlyIn:0, currentlyOut:0 });
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [isDirty,     setIsDirty]     = useState(false);
  const [lastSaved,   setLastSaved]   = useState('');
  const [editingCell, setEditingCell] = useState(null); // { day, meal }
  const [editVals,    setEditVals]    = useState({ name: '', items: '' });
  const editRef = useRef(null);

  // ── load calendar month ───────────────────────────────────────────────────
  const loadCalMonth = useCallback(async (y, m) => {
    try {
      const data = await canteenService.getMonthMenus(y, m);
      setMenus(buildLookup(data));
    } catch(e) { console.error(e); }
  }, []);

  // ── load planner month ────────────────────────────────────────────────────
  const loadPlanMonth = useCallback(async (y, m) => {
    try {
      const [data, status] = await Promise.all([
        canteenService.getMonthMenus(y, m),
        canteenService.getMonthStatus(y, m),
      ]);
      setPlanMenus(buildLookup(data));
      setMonthStatus(status.status || 'EMPTY');
    } catch(e) { console.error(e); }
  }, []);

  // ── load today ─────────────────────────────────────────────────────────────
  useEffect(() => {
    canteenService.getToday().then(data => setTodayMenus(buildLookup(data))).catch(()=>{});
    // Fetch real admission stats
    api.get('/admissions/stats').then(r => setAdmStats(r.data)).catch(()=>{});
  }, []);

  useEffect(() => { loadCalMonth(calYear, calMonth); }, [calYear, calMonth, loadCalMonth]);
  useEffect(() => { if (view === 'planner') loadPlanMonth(planYear, planMonth); }, [planYear, planMonth, view, loadPlanMonth]);

  // ── calendar nav ──────────────────────────────────────────────────────────
  const prevCalMonth = () => { if (calMonth === 1) { setCalYear(y => y-1); setCalMonth(12); } else setCalMonth(m => m-1); };
  const nextCalMonth = () => { if (calMonth === 12) { setCalYear(y => y+1); setCalMonth(1); } else setCalMonth(m => m+1); };
  const prevPlanMonth = () => { if (planMonth === 1) { setPlanYear(y => y-1); setPlanMonth(12); } else setPlanMonth(m => m-1); };
  const nextPlanMonth = () => { if (planMonth === 12) { setPlanYear(y => y+1); setPlanMonth(1); } else setPlanMonth(m => m+1); };

  // ── planner edit ─────────────────────────────────────────────────────────
  const startEdit = (day, meal, currentData) => {
    if (editingCell) commitEdit(editingCell.day, editingCell.meal);
    setEditingCell({ day, meal });
    if (meal === 'note') {
      // note is a plain string, not an object
      setEditVals({ name: typeof currentData === 'string' ? currentData : '', items: '' });
    } else {
      setEditVals({
        name:  currentData?.name  || '',
        items: (currentData?.items || []).join(' · '),
      });
    }
    setTimeout(() => editRef.current?.focus(), 50);
  };

  const commitEdit = useCallback(async (day, meal) => {
    setEditingCell(null);
    const date = fmtDate(planYear, planMonth, day);
    // Handle note separately — stored as plain string
    if (meal === 'note') {
      setPlanMenus(prev => {
        const updated = { ...prev };
        if (!updated[date]) updated[date] = {};
        if (!editVals.name.trim()) { delete updated[date].note; }
        else { updated[date].note = editVals.name.trim(); }
        return updated;
      });
      setIsDirty(true);
      return;
    }
    if (!editVals.name.trim() && !editVals.items.trim()) return;
    const items = editVals.items ? editVals.items.split('·').map(s => s.trim()).filter(Boolean) : [];
    // update local state immediately
    setPlanMenus(prev => {
      const updated = { ...prev };
      if (!updated[date]) updated[date] = {};
      if (!editVals.name.trim()) { delete updated[date][meal]; }
      else { updated[date][meal] = { name: editVals.name.trim(), items }; }
      return updated;
    });
    setIsDirty(true);
  }, [editVals, planYear, planMonth]);

  const handleCellClick = (day, meal) => {
    const key = fmtDate(planYear, planMonth, day);
    const current = planMenus[key]?.[meal];
    startEdit(day, meal, current);
  };

  // ── keyboard nav in planner ───────────────────────────────────────────────
  const handleKeyDown = (e, day, meal) => {
    const cols = ['b', 'l', 'd'];
    const ci   = cols.indexOf(meal);
    const dim  = daysInMonth(planYear, planMonth);
    if (e.key === 'Escape') { setEditingCell(null); return; }
    if (e.key === 'Tab') {
      e.preventDefault();
      commitEdit(day, meal);
      if (!e.shiftKey) {
        if (ci < cols.length - 1) startEdit(day, cols[ci + 1], planMenus[fmtDate(planYear, planMonth, day)]?.[cols[ci+1]]);
        else if (day < dim) startEdit(day + 1, cols[0], planMenus[fmtDate(planYear, planMonth, day+1)]?.b);
      } else {
        if (ci > 0) startEdit(day, cols[ci - 1], planMenus[fmtDate(planYear, planMonth, day)]?.[cols[ci-1]]);
        else if (day > 1) startEdit(day - 1, cols[cols.length-1], planMenus[fmtDate(planYear, planMonth, day-1)]?.d);
      }
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit(day, meal);
      if (day < dim) startEdit(day + 1, meal, planMenus[fmtDate(planYear, planMonth, day+1)]?.[meal]);
    }
  };

  // ── save draft ────────────────────────────────────────────────────────────
  const saveDraft = async () => {
    if (editingCell) await commitEdit(editingCell.day, editingCell.meal);
    setSaving(true);
    try {
      const mealsToSave = buildPayload(planMenus, planYear, planMonth, 'DRAFT');
      await canteenService.bulkSave(mealsToSave, 'DRAFT');
      setIsDirty(false);
      setMonthStatus('DRAFT');
      setLastSaved(`Draft saved at ${new Date().toLocaleTimeString()}`);
      // Sync calendar view immediately
      if (calYear === planYear && calMonth === planMonth) setMenus({ ...planMenus });
    } catch(e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  // ── publish ────────────────────────────────────────────────────────────────
  const publishMenu = async () => {
    if (editingCell) await commitEdit(editingCell.day, editingCell.meal);
    setSaving(true);
    try {
      const mealsToSave = buildPayload(planMenus, planYear, planMonth, 'PUBLISHED');
      await canteenService.bulkSave(mealsToSave, 'PUBLISHED');
      await canteenService.publishMonth(planYear, planMonth, 'PUBLISHED');
      setIsDirty(false);
      setMonthStatus('PUBLISHED');
      setLastSaved(`Published at ${new Date().toLocaleTimeString()}`);
      // Sync calendar view immediately
      if (calYear === planYear && calMonth === planMonth) setMenus({ ...planMenus });
    } catch(e) { alert('Publish failed: ' + e.message); }
    finally { setSaving(false); }
  };

  // ── unpublish ─────────────────────────────────────────────────────────────
  const unpublish = async () => {
    setSaving(true);
    try {
      await canteenService.publishMonth(planYear, planMonth, 'DRAFT');
      setMonthStatus('DRAFT');
    } catch(e) { alert('Failed: ' + e.message); }
    finally { setSaving(false); }
  };

  // ── copy last month ────────────────────────────────────────────────────────
  const copyLastMonth = async () => {
    const srcM = planMonth === 1 ? 12 : planMonth - 1;
    const srcY = planMonth === 1 ? planYear - 1 : planYear;
    try {
      const data = await canteenService.getMonthMenus(srcY, srcM);
      const srcLookup = buildLookup(data);
      const dim = daysInMonth(planYear, planMonth);
      const srcDim = daysInMonth(srcY, srcM);
      const newMenus = { ...planMenus };
      let copied = 0;
      for (let d = 1; d <= dim; d++) {
        const srcDay = Math.min(d, srcDim);
        const srcKey = fmtDate(srcY, srcM, srcDay);
        const dstKey = fmtDate(planYear, planMonth, d);
        if (srcLookup[srcKey]) { newMenus[dstKey] = JSON.parse(JSON.stringify(srcLookup[srcKey])); copied++; }
      }
      setPlanMenus(newMenus);
      setIsDirty(true);
      alert(`Copied ${copied} days from ${MONTH_NAMES[srcM-1]} ${srcY}. Review and edit as needed.`);
    } catch(e) { alert('Copy failed: ' + e.message); }
  };

  // ── clear month ────────────────────────────────────────────────────────────
  const clearMonth = async () => {
    if (!window.confirm(`Clear all menu data for ${MONTH_NAMES[planMonth-1]} ${planYear}?`)) return;
    setSaving(true);
    try {
      await canteenService.clearMonth(planYear, planMonth);
      setPlanMenus({});
      setMonthStatus('EMPTY');
      setIsDirty(false);
    } catch(e) { alert('Clear failed: ' + e.message); }
    finally { setSaving(false); }
  };

  // ── helper: build payload from planMenus ──────────────────────────────────
  function buildPayload(lookup, y, m, status) {
    const out = [];
    Object.entries(lookup).forEach(([date, day]) => {
      ['b','l','d'].forEach(meal => {
        if (day[meal]?.name) {
          out.push({
            date,
            mealType: meal === 'b' ? 'BREAKFAST' : meal === 'l' ? 'LUNCH' : 'DINNER',
            mealName: day[meal].name,
            items: day[meal].items || [],
            specialNote: day.note || null,
            status,
          });
        }
      });
    });
    return out;
  }

  // ── today's data ──────────────────────────────────────────────────────────
  const todayKey = fmtDate(TODAY.getFullYear(), TODAY.getMonth()+1, TODAY.getDate());
  const todayData = todayMenus[todayKey] || {};

  // ── render calendar grid ──────────────────────────────────────────────────
  const renderCalendarGrid = () => {
    const firstDow = new Date(calYear, calMonth - 1, 1).getDay();
    const dim = daysInMonth(calYear, calMonth);
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(<div key={`e${i}`} className="cal-cell empty" />);
    for (let d = 1; d <= dim; d++) {
      const key = fmtDate(calYear, calMonth, d);
      const menu = menus[key] || {};
      const isToday = d === TODAY.getDate() && calMonth === TODAY.getMonth()+1 && calYear === TODAY.getFullYear();
      cells.push(
        <div key={d} className={`cal-cell${isToday ? ' today' : ''}`}>
          <div className="date-num">{d}</div>
          {(activeTab === 'all' || activeTab === 'breakfast') && <MealPill type="b" data={menu.b}/>}
          {(activeTab === 'all' || activeTab === 'lunch')     && <MealPill type="l" data={menu.l}/>}
          {(activeTab === 'all' || activeTab === 'dinner')    && <MealPill type="d" data={menu.d}/>}
          {menu.note && <div className="cal-note">★ {menu.note}</div>}
          {!menu.b && !menu.l && !menu.d && <div className="no-menu">—</div>}
        </div>
      );
    }
    return cells;
  };

  // ── status chip ───────────────────────────────────────────────────────────
  const StatusChip = () => {
    if (monthStatus === 'PUBLISHED') return <span className="status-chip chip-published">✓ PUBLISHED</span>;
    if (monthStatus === 'DRAFT')     return <span className="status-chip chip-draft">DRAFT — NOT PUBLISHED</span>;
    return <span className="status-chip chip-empty">NO MENU</span>;
  };

  return (
    <div className="app-layout">
      <Sidebar activeItem="canteen" onNavigate={onNavigate} onLogout={onLogout}/>
      <main className="main-content" style={{ padding:0 }}>

        {/* ── TOPBAR ── */}
        <div className="canteen-topbar" style={{ position:"sticky", top:0, zIndex:50 }}>
          <div className="topbar-breadcrumb">Operations / <span>Canteen</span></div>
          <div className="topbar-right">
            <div className="date-badge">
              {TODAY.toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
            </div>
            <button
              className={`btn-outline${view === 'calendar' ? ' active' : ''}`}
              onClick={() => setView('calendar')}>⊞ Calendar View
            </button>
            <button
              className={`btn-gold${view === 'planner' ? ' active-gold' : ''}`}
              onClick={() => setView('planner')}>✎ PLAN MONTH
            </button>
          </div>
        </div>

        <div className="canteen-content">

          {/* ══════════════════════════════════════════════════════════════════
              CALENDAR VIEW
          ══════════════════════════════════════════════════════════════════ */}
          {view === 'calendar' && (
            <>
              <div className="page-header">
                <div>
                  <div className="page-title">Canteen Menu</div>
                  <div className="page-subtitle">Monthly meal planner — breakfast, lunch &amp; dinner</div>
                </div>
              </div>

              {/* Stats */}
              <div className="strength-row">
                <div className="strength-card navy-top">
                  <div className="strength-label">Total Enrolled</div>
                  <div className="strength-num">{admStats.allocated + admStats.total > 0 ? admStats.allocated || admStats.total : '—'}</div>
                  <div className="strength-sub">active students on roll</div>
                  <div className="strength-badge">🏠</div>
                </div>
                <div className="strength-card green-top">
                  <div className="strength-label">In Premises — Today</div>
                  <div className="strength-num green">{admStats.currentlyIn || '—'}</div>
                  <div className="strength-sub">as of last biometric</div>
                  <div className="strength-badge">✅</div>
                </div>
                <div className="strength-card gold-top">
                  <div className="strength-label">Expected Tomorrow</div>
                  <div className="strength-num gold">{admStats.allocated > 0 ? admStats.allocated : '—'}</div>
                  <div className="strength-sub">based on leave status</div>
                  <div className="strength-badge">📅</div>
                </div>
                <div className="strength-card red-top">
                  <div className="strength-label">On Leave / Away</div>
                  <div className="strength-num red">{admStats.currentlyOut || '—'}</div>
                  <div className="strength-sub">off-premises students</div>
                  <div className="strength-badge">🚪</div>
                </div>
              </div>

              {/* Today panel */}
              <div className="today-panel">
                {[
                  { key:'b', type:'☀ Breakfast', time:'7:00 AM – 9:00 AM' },
                  { key:'l', type:'🌤 Lunch',     time:'12:30 PM – 2:30 PM' },
                  { key:'d', type:'🌙 Dinner',    time:'7:30 PM – 9:30 PM' },
                ].map((slot, i) => (
                  <React.Fragment key={slot.key}>
                    {i > 0 && <div className="divider-v"/>}
                    <div className="today-meal">
                      <div className="today-meal-type">{slot.type}</div>
                      {todayData[slot.key] ? (
                        <>
                          <div className="today-meal-name">{todayData[slot.key].name}</div>
                          {(todayData[slot.key].items || []).map((item, j) => (
                            <div key={j} className="today-item">{item}</div>
                          ))}
                        </>
                      ) : (
                        <div className="today-meal-name" style={{ opacity:0.4, fontSize:14 }}>Not planned</div>
                      )}
                      <div className="today-time">{slot.time}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>

              {/* Month nav + filter chips (single combined row) */}
              <div className="month-nav">
                <button className="nav-btn" onClick={prevCalMonth}>‹</button>
                <div className="month-nav-title">{MONTH_NAMES[calMonth-1]} {calYear}</div>
                <button className="nav-btn" onClick={nextCalMonth}>›</button>
                <div className="meal-toggle" style={{ marginLeft:'auto' }}>
                  {[
                    { v:'all',       label:'All' },
                    { v:'breakfast', label:'🌅 Breakfast' },
                    { v:'lunch',     label:'☀ Lunch' },
                    { v:'dinner',    label:'🌙 Dinner' },
                  ].map(chip => (
                    <div key={chip.v} className={`meal-chip${activeTab===chip.v?' active':''}`}
                      onClick={() => setActiveTab(chip.v)}>{chip.label}</div>
                  ))}
                </div>
              </div>

              {/* Legend */}
              <div className="legend">
                <span className="legend-heading">Legend:</span>
                <div className="legend-item"><div className="legend-dot breakfast-dot"/><span>Breakfast</span></div>
                <div className="legend-item"><div className="legend-dot lunch-dot"/><span>Lunch</span></div>
                <div className="legend-item"><div className="legend-dot dinner-dot"/><span>Dinner</span></div>
                <span className="legend-hint">Click any date to view details</span>
              </div>

              {/* Calendar */}
              <div className="calendar-wrap">
                <div className="cal-header">
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                    <div key={d} className={`cal-dow${d==='Sat'?' weekend':''}`}>{d}</div>
                  ))}
                </div>
                <div className="cal-grid">{renderCalendarGrid()}</div>
              </div>
            </>
          )}

          {/* ══════════════════════════════════════════════════════════════════
              PLANNER VIEW
          ══════════════════════════════════════════════════════════════════ */}
          {view === 'planner' && (
            <>
              <div className="planner-toolbar">
                <div>
                  <div className="page-title" style={{ fontSize:22 }}>Monthly Menu Planner</div>
                  <div className="page-subtitle">Click any cell to edit · Tab · Enter for next row</div>
                </div>
                <div className="planner-actions">
                  <div className="planner-month-nav">
                    <button className="nav-btn" onClick={prevPlanMonth}>‹</button>
                    <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, fontWeight:600, color:'#0f1a30', minWidth:120, textAlign:'center', whiteSpace:'nowrap' }}>{MONTH_NAMES[planMonth-1]} {planYear}</div>
                    <button className="nav-btn" onClick={nextPlanMonth}>›</button>
                  </div>
                  <StatusChip/>
                  <button className="btn-outline btn-sm" onClick={copyLastMonth} disabled={saving}>⎘ COPY LAST MONTH</button>
                  <button className="btn-outline btn-sm btn-danger" onClick={clearMonth} disabled={saving}>✕ CLEAR</button>
                  <button style={{ background:'#1a2744', color:'#fff', border:'none', padding:'8px 14px', fontSize:11, fontWeight:600, letterSpacing:'.8px', textTransform:'uppercase', cursor:'pointer', borderRadius:2, whiteSpace:'nowrap', opacity:saving?0.6:1 }} onClick={saveDraft} disabled={saving}>{saving?'SAVING…':'SAVE DRAFT'}</button>
                  <button className="btn-gold btn-sm-gold" onClick={publishMenu} disabled={saving}>{saving?'…':'✓ PUBLISH'}</button>
                </div>
              </div>

              {/* Banners */}
              {monthStatus === 'PUBLISHED' && (
                <div className="publish-notice">
                  <span>✓</span>
                  Menu published for <strong>{MONTH_NAMES[planMonth-1]} {planYear}</strong>. Visible on student app &amp; notifications.
                  <button className="unpublish-btn" onClick={unpublish}>UNPUBLISH</button>
                </div>
              )}
              {isDirty && (
                <div className="draft-notice">
                  <span>⚠</span>
                  You have unsaved changes — click <strong>Save Draft</strong> or <strong>Publish</strong>.
                </div>
              )}

              {/* Grid */}
              <>
                  <div className="grid-scroll-wrap">
                    <table className="plan-table">
                      <thead>
                        <tr>
                          <th className="th-day">#</th>
                          <th className="th-date">Date</th>
                          <th className="th-b">☀ Breakfast <span className="th-time">7:00 – 9:00 AM</span></th>
                          <th className="th-l">🌤 Lunch <span className="th-time">12:30 – 2:30 PM</span></th>
                          <th className="th-d">🌙 Dinner <span className="th-time">7:30 – 9:30 PM</span></th>
                          <th className="th-note">★ Special Note / Festival</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: daysInMonth(planYear, planMonth) }, (_, i) => i + 1).map(d => {
                          const date   = new Date(planYear, planMonth - 1, d);
                          const dow    = date.getDay();
                          const isToday = d === TODAY.getDate() && planMonth === TODAY.getMonth()+1 && planYear === TODAY.getFullYear();
                          const isWeekend = dow === 0 || dow === 6;
                          const key = fmtDate(planYear, planMonth, d);
                          const dayData = planMenus[key] || {};
                          const rowCls = isToday ? 'today-row' : isWeekend ? 'weekend-row' : '';
                          return (
                            <tr key={d} className={rowCls}>
                              <td className="td-day">
                                {isToday
                                  ? <span className="today-tag">TODAY</span>
                                  : DAY_NAMES[dow]}
                              </td>
                              <td className="td-date">{d} {MONTH_NAMES[planMonth-1].slice(0,3)}</td>
                              {/* Breakfast */}
                              {['b','l','d'].map(meal => {
                                const mdata   = dayData[meal];
                                const isEditing = editingCell?.day === d && editingCell?.meal === meal;
                                const filled  = !!mdata?.name;
                                const mealLabel = meal==='b'?'breakfast':meal==='l'?'lunch':'dinner';
                                return (
                                  <td key={meal} className={`meal-cell ${meal}-cell${filled?' filled':''}${isEditing?' editing':''}`}
                                    onClick={() => !isEditing && handleCellClick(d, meal)}>
                                    {isEditing ? (
                                      <div className={`cell-edit ${meal}-edit`}>
                                        <input
                                          ref={editRef}
                                          className="name-inp"
                                          placeholder={`Meal name e.g. ${meal==='b'?'Poha & Chai':meal==='l'?'Dal-Rice Thali':'Rajma Chawal'}`}
                                          value={editVals.name}
                                          onChange={e => setEditVals(v => ({ ...v, name: capFirst(e.target.value) }))}
                                          onKeyDown={e => handleKeyDown(e, d, meal)}
                                        />
                                        <input
                                          className="items-inp"
                                          placeholder="Items: Item1 · Item2 · Item3"
                                          value={editVals.items}
                                          onChange={e => setEditVals(v => ({ ...v, items: e.target.value.split('·').map((s,i) => i === 0 || s.trim() ? (s.trimStart().charAt(0).toUpperCase() + s.trimStart().slice(1)) : s).join('·') }))}
                                          onKeyDown={e => handleKeyDown(e, d, meal)}
                                        />
                                        <div className="cell-hint">Name on top · items below separated by ·</div>
                                      </div>
                                    ) : (
                                      <div className="cell-display">
                                        {filled ? (
                                          <>
                                            <div className="cell-val">{mdata.name}</div>
                                            <div className="cell-items">{(mdata.items||[]).join(' · ')}</div>
                                          </>
                                        ) : (
                                          <div className="cell-empty">Click to add {mealLabel}…</div>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                              {/* Note */}
                              <td className="td-note" onClick={() => handleCellClick(d, 'note')}>
                                {editingCell?.day === d && editingCell?.meal === 'note' ? (
                                  <input
                                    className="note-inp"
                                    placeholder="e.g. Holi Special — Thandai served"
                                    value={editVals.name}
                                    onChange={e => setEditVals(v => ({ ...v, name: capFirst(e.target.value) }))}
                                    onKeyDown={e => { if(e.key==='Enter'||e.key==='Escape') commitEdit(d,'note'); }}
                                    autoFocus
                                  />
                                ) : (
                                  <div className="note-display">{dayData.note || ''}</div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="grid-footer">
                    <div className="kbd-hints">
                      <kbd>Tab</kbd> next cell &nbsp;
                      <kbd>Shift+Tab</kbd> prev &nbsp;
                      <kbd>Enter</kbd> next row &nbsp;
                      <kbd>Esc</kbd> cancel
                    </div>
                    <div className="last-saved">{lastSaved}</div>
                  </div>
              </>
            </>
          )}
        </div>
      </main>
    </div>
  );
}